import { createTheme } from '@mui/material/styles'

export const DrawerWidth = 250

/**
 * ELECTROBRAID UNIFIED COLOR SYSTEM - Version 1.0
 *
 * This is the single source of truth for all colors in the application.
 * All components should reference these colors instead of using literal hex values.
 *
 * Organization:
 * - Primary Brand Colors (main identity colors)
 * - Brand Color Variants (shades and tints)
 * - Background Colors (page and section backgrounds)
 * - Accent Colors (decorative elements)
 * - State Colors (success, error, warning, info)
 * - Grayscale (neutral colors)
 * - Text Colors (typography colors)
 * - Legacy Support (backward compatibility with companyColors)
 */

export const Colors = {
    // ========================================
    // PRIMARY BRAND COLORS
    // ========================================
    primary: '#fcf6f0', // Light cream - container background
    secondary: '#005247', // Dark green - primary brand color

    // ========================================
    // BRAND COLOR VARIANTS
    // ========================================
    greenLight: '#3cbe72', // Light green - hover states, links
    greenMedium: '#0f6453', // Medium green - accents, borders
    greenDark: '#006e64', // Dark green - borders, shadows
    greenDarker: '#007061', // Darker green - strong borders

    // ========================================
    // BACKGROUND COLORS
    // ========================================
    cream: '#fef4e9', // Cream - primary backgrounds, cards
    creamLight: '#ffe9cf', // Light cream - containers (same as primary)
    beige: '#fcf6f0', // Beige - navbar, footer sections
    white: '#fff', // Pure white - paper, cards
    black: '#000', // Pure black - text, borders

    // ========================================
    // ACCENT  c5a059
    // ========================================
    boltGold: '#B98647', // Primary bolt/gold accent
    boltGold2: '#B19470', // Secondary bolt/gold
    boltTan: '#b8956f', // Tan variant for headers
    copper: '#b87333', // Copper accent
    brown: '#825735', // Brown accent
    brownDark: '#76453B', // Dark brown
    orange: '#d87c28', // Orange accent
    orangeLight1: '#f6c995', // Light orange
    orangeLight2: '#ffe9cf', // Lightest orange (same as creamLight)

    // ========================================
    // STATE COLORS
    // ========================================
    success: '#4CAF50', // Success state (green)
    info: '#00a2ff', // Info state (blue)
    danger: '#FF5722', // Danger/error state (red)
    warning: '#d87c28', // Warning state (orange)
    accent: '#800000', // Maroon accent
    red: '#800000', // Red accent (same as accent)
    redLight: '#edc1c4', // Light red
    yellow: '#fff179', // Yellow highlight

    // ========================================
    // GRAYSCALE
    // ========================================
    grayLight: '#cccccc', // Light gray - borders, dividers
    grayMedium: '#8796A5', // Medium gray - secondary text
    grayDark: '#696969', // Dark gray - muted text
    grayDove: '#d5d5d5', // Dove gray - light borders
    grayBody: '#f3f6f9', // Body background gray
    grayBorder: '#d8d8d8', // Standard border gray
    grayBorderDark: '#aab4be', // Dark border gray
    grayMuted: '#abafb3', // Muted text gray
    shaft: '#333', // Very dark gray (footer bg)
    dark: '#0e1b20', // Near black
    light: '#aaa', // Light gray text

    // ========================================
    // TEXT COLORS
    // ========================================
    textPrimary: '#000', // Primary text color
    textSecondary: '#825735', // Secondary text (brown)
    textMuted: '#666', // Muted text
    textContrast: '#FEFDED', // Light text on dark bg

    // ========================================
    // BORDERS
    // ========================================
    border: '#d8d8d8', // Standard border color
    borderLight: '#DDDFE1', // Light border
    borderDark: '#aab4be', // Dark border

    // ========================================
    // SPECIAL PURPOSE
    // ========================================
    inverse: '#2F3D4A', // Inverse color for special elements

    // ========================================
    // LEGACY SUPPORT (Backward Compatibility)
    // Maintained for existing components
    // Gradually migrate to top-level colors
    // ========================================
    companyColors: {
        eb_text: '#004F39',
        eb_bkgrnd: '#fef4e9',
        eb_bolt: '#B98647',
        eb_bolt2: '#B19470',
        eb_bolt3: '#b8956f', // NEW - tan variant

        eb_text_service: '#43766C',
        eb_bkgrnd_service: '#F8FAE5',
        eb_bolt_service: '#B19470',
        eb_bolt_service2: '#76453B',
        eb_bolt_service3: '#76453B',

        accent: '#800000',

        greenLight: '#3cbe72',
        greenDark: '#006e64',
        contrastTextWhite: '#FEFDED',

        beige: '#ffe9cf',
        brown: '#825735',
        orange: '#d87c28',
        orangeL1: '#f6c995',
        orangeL2: '#ffe9cf',
        copper: '#b87333',

        yellow: '#fff179',
        cream: '#fef4e9',
        red: '#800000',
        redL: '#edc1c4',
    },

    // Legacy grayscale (kept for compatibility)
    dim_grey: '#696969',
    dove_gray: '#d5d5d5',
    body_bg: '#f3f6f9',
    light_gray: 'rgb(230,230,230)',
    contrastTextBlack: '#000',
}

const theme = createTheme({
    palette: {
        primary: {
            main: Colors.primary,
            light: Colors.creamLight,
            dark: Colors.brown,
        },
        secondary: {
            main: Colors.secondary,
            light: Colors.greenLight,
            dark: Colors.greenDark,
        },
        background: {
            default: Colors.white,
            paper: Colors.white,
        },
        text: {
            primary: Colors.textPrimary,
            secondary: Colors.textSecondary,
        },
        divider: Colors.border,
        success: {
            main: Colors.success,
        },
        error: {
            main: Colors.danger,
        },
        warning: {
            main: Colors.warning,
        },
        info: {
            main: Colors.info,
        },
    },

    breakpoints: {
        values: {
            xs: 0,
            sm: 640,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },

    components: {
        MuiCssBaseline: {
            styleOverrides: `
                body {
                    margin: 0 !important;
                    padding: 0 !important;
                }
            `,
        },
        MuiButton: {
            defaultProps: {
                disableRipple: true,
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    background: Colors.secondary,
                    color: 'white',
                    border: `2px solid ${Colors.boltGold}`,
                    '&:hover': {
                        backgroundColor: Colors.greenLight,
                    },
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                arrow: true,
            },
            styleOverrides: {
                tooltip: {
                    background: Colors.primary,
                },
                arrow: {
                    color: Colors.primary,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    width: DrawerWidth,
                    background: Colors.primary,
                    color: Colors.secondary,
                    borderRadius: '0px 100px 0px 0px',
                    borderRight: `1px solid ${Colors.primary}`,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: Colors.border,
                },
            },
        },
        MyShopButton: {
            styleOverrides: {
                root: {
                    color: Colors.white,
                },
                primary: {
                    background: Colors.secondary,
                    '&:hover': {
                        backgroundColor: Colors.greenLight,
                    },
                },
                secondary: {
                    background: Colors.primary,
                    '&:hover': {
                        backgroundColor: Colors.greenLight,
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    padding: '2px 3px',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    ':hover': {
                        textDecoration: 'underline',
                        fontSize: '103%',
                        backgroundColor: Colors.secondary,
                        color: Colors.primary,
                        fontWeight: '500',
                    },
                },
            },
        },
    },
})

export default theme
